import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../api/problem";
import { formatDateTime, formatDay } from "../../i18n/format";
import { Button } from "../../ui/Button";
import { LongList } from "../../ui/LongList";
import { alarmDevicesQuery } from "../alarms/queries";
import { ConfirmDialog } from "../ops/shared/ConfirmDialog";
import { useOrg } from "../org/OrgProvider";
import { isAdminRole } from "../organisation/roles";
import { organisationQuery } from "../organisation/queries";
import { answerRequest, casesQuery, supportGrantsQuery, supportRequestsQuery } from "./queries";
import {
  accessEnds,
  clampDays,
  DEFAULT_DAYS,
  levelsUpTo,
  MAX_DAYS,
  supportError,
  type AccessRequest,
  type GrantLevel,
} from "./support";

/** The requests waiting for this person: only Owners and Admins are asked (WEB-SUP-REQ-01). */
function useRequests() {
  const { orgId, current } = useOrg();
  const admin = isAdminRole(current?.role);
  return { admin, requests: useQuery(supportRequestsQuery(orgId ?? "", admin)) };
}

/** The count of requests waiting, next to Help in the navigation (WEB-SUP-REQ-01); nothing while there are none. */
export function HelpBadge() {
  const { t } = useTranslation();
  const { admin, requests } = useRequests();
  const count = requests.data?.length ?? 0;
  if (!admin || count === 0) return null;
  return (
    <>
      <span aria-hidden="true" className="ml-auto rounded-full bg-amber-200 px-2 text-xs font-semibold text-gray-900">
        {count}
      </span>
      <span className="sr-only">{t("support.requests.badge", { count })}</span>
    </>
  );
}

/**
 * Requests waiting for you (WEB-SUP-REQ-01, -02), at the top of Help for Owners and Admins: support asking to see a
 * device for a call without an app case, and members' cases that need someone to approve access. Allow opens the
 * consent screen preset to what was asked, which may be lowered or shortened; Deny asks to confirm. Only this answer
 * grants access (SUP-24).
 */
export function RequestsSection() {
  const { t, i18n } = useTranslation();
  const { orgId } = useOrg();
  const org = orgId ?? "";
  const qc = useQueryClient();
  const { admin, requests } = useRequests();
  const devices = useQuery(alarmDevicesQuery(org));
  const [allowing, setAllowing] = useState<AccessRequest>();
  const [denying, setDenying] = useState<AccessRequest>();
  const deny = useMutation({
    mutationFn: (r: AccessRequest) => answerRequest(r.id, null),
    onSuccess: () => {
      setDenying(undefined);
      void qc.invalidateQueries({ queryKey: supportRequestsQuery(org, true).queryKey });
      void qc.invalidateQueries({ queryKey: casesQuery(org).queryKey });
      void qc.invalidateQueries({ queryKey: supportGrantsQuery(org).queryKey });
    },
  });
  if (!admin || !requests.data || requests.data.length === 0) {
    // A server without the requests route (404, 501): My cases below already says support is not available here.
    const missing =
      requests.error instanceof ApiError && (requests.error.status === 404 || requests.error.status === 501);
    return requests.isError && !missing ? (
      <p role="alert" className="mt-4 text-sm text-red-900">
        {supportError(requests.error, t)}
      </p>
    ) : null;
  }
  const name = (id: string) => {
    const d = devices.data?.get(id);
    return d ? (d.name ?? d.serial ?? d.device_id) : id.slice(0, 8);
  };
  return (
    <section aria-labelledby="support-requests" className="mt-6">
      <h2 id="support-requests" className="font-semibold text-gray-900">
        {t("support.requests.title")}
      </h2>
      <LongList
        items={requests.data}
        rowKey={(r) => r.id}
        estimate={112}
        className="mt-2 space-y-2"
        itemClassName="pb-2"
        row={(r) => (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
            <p>{requestText(r, name, t)}</p>
            <p className="mt-1 text-xs text-gray-700">
              {t(`support.level.${r.level}`)} · {formatDateTime(r.created_at, i18n.language)}
            </p>
            <div className="mt-2 flex gap-2">
              <Button className="px-3 py-1" onClick={() => setAllowing(r)}>
                {t("support.requests.allow")}
              </Button>
              <Button variant="secondary" className="px-3 py-1" onClick={() => setDenying(r)}>
                {t("support.requests.deny")}
              </Button>
            </div>
          </div>
        )}
      />
      {allowing && <AllowDialog request={allowing} name={name} onClose={() => setAllowing(undefined)} />}
      <ConfirmDialog
        open={!!denying}
        title={t("support.requests.denyTitle")}
        confirmLabel={t("support.requests.deny")}
        ready={!deny.isPending}
        onConfirm={() => denying && deny.mutate(denying)}
        onClose={() => setDenying(undefined)}
      >
        <p>{t("support.requests.denyText")}</p>
        {deny.isError && (
          <p role="alert" className="mt-2 text-red-900">
            {supportError(deny.error, t)}
          </p>
        )}
      </ConfirmDialog>
    </section>
  );
}

type T = (key: string, options?: Record<string, unknown>) => string;

/** A request in words: who asks, to see which devices, for which case (SUP-24, SUP-22). */
export function requestText(r: AccessRequest, name: (id: string) => string, t: T): string {
  const devices = r.device_ids.map(name).join(", ");
  if (r.origin === "support") {
    const who = r.requested_by.handle
      ? t("support.staffHandle", { handle: r.requested_by.handle })
      : t("support.staff");
    return t("support.requests.fromSupport", { who, devices, case: r.case_number });
  }
  return t("support.requests.fromMember", {
    who: r.requested_by.name ?? t("support.customer"),
    devices,
    case: r.case_number,
  });
}

/** Allow: the consent screen preset to what was asked; a lower level or a shorter length may be chosen (REQ-02). */
function AllowDialog({
  request: r,
  name,
  onClose,
}: {
  request: AccessRequest;
  name: (id: string) => string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { orgId } = useOrg();
  const org = orgId ?? "";
  const qc = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const ids = { title: useId(), days: useId(), ends: useId() };
  const organisation = useQuery(organisationQuery(org));
  const levels = levelsUpTo(r.level, organisation.data?.remote_diagnostics);
  // The highest level allowed is preset until the person picks one; a pick no longer allowed (the organisation's setting
  // arrived and says never) falls back to the highest allowed.
  const [picked, setPicked] = useState<GrantLevel>();
  const level = picked && levels.includes(picked) ? picked : (levels[levels.length - 1] ?? "health");
  const [days, setDays] = useState(String(DEFAULT_DAYS));
  const allow = useMutation({
    mutationFn: () => answerRequest(r.id, { level, valid_days: clampDays(Number(days)) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supportRequestsQuery(org, true).queryKey });
      void qc.invalidateQueries({ queryKey: casesQuery(org).queryKey });
      void qc.invalidateQueries({ queryKey: supportGrantsQuery(org).queryKey });
      onClose();
    },
  });
  useEffect(() => {
    const d = dialog.current;
    if (d && !d.open) d.showModal();
  }, []);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!allow.isPending && days) allow.mutate();
  }
  const ends = formatDay(accessEnds(Number(days) || DEFAULT_DAYS, new Date()), i18n.language);
  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={ids.title}
      className="m-auto w-full max-w-lg rounded-lg p-0 shadow-xl backdrop:bg-black/40"
    >
      <form onSubmit={submit} className="space-y-4 p-6 text-sm">
        <h2 id={ids.title} className="text-lg font-semibold text-gray-900">
          {t("support.requests.allowTitle")}
        </h2>
        <p className="text-gray-800">{requestText(r, name, t)}</p>
        <fieldset className="space-y-2">
          <legend className="font-medium">{t("support.requests.level")}</legend>
          {levels.map((l) => (
            <label key={l} className="flex items-center gap-2">
              <input type="radio" name={`${ids.title}-level`} checked={level === l} onChange={() => setPicked(l)} />
              {t(`support.levelLong.${l}`)}
            </label>
          ))}
        </fieldset>
        <div>
          <label htmlFor={ids.days} className="block">
            {t("support.consent.days")}
          </label>
          <input
            id={ids.days}
            type="number"
            min={1}
            max={MAX_DAYS}
            required
            value={days}
            aria-describedby={ids.ends}
            onChange={(e) => setDays(e.target.value)}
            className="mt-1 w-20 rounded-md border border-gray-300 px-2 py-1.5"
          />
          <p id={ids.ends} aria-live="polite" className="mt-1 text-gray-800">
            {t("support.consent.ends", { date: ends })}
          </p>
        </div>
        {allow.isError && (
          <p role="alert" className="text-red-900">
            {supportError(allow.error, t)}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={allow.isPending || !days}>
            {t("support.requests.allow")}
          </Button>
        </div>
      </form>
    </dialog>
  );
}
